import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { forkJoin, Observable } from 'rxjs';
import { FormsModule } from '@angular/forms';

import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzModalModule, NzModalService } from 'ng-zorro-antd/modal';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { NzAvatarModule } from 'ng-zorro-antd/avatar';
import { TranslateModule } from '@ngx-translate/core';
import { TranslateService } from '@ngx-translate/core';

import {
  IntegrationsService,
  IntegrationCapability,
  IntegrationConnection,
  IntegrationProviderCatalogItem,
} from './integrations.service';
import { AuthService } from '../services/auth.service';
import { shouldSuppressGenericError } from '../utils/http-error.utils';
import { formatSftpDateTime as formatSftpDateTimeValue, SftpDateTimeValue } from './sftp-date-time.utils';

type StatusFilter = 'all' | 'connected' | 'not_connected' | 'needs_attention';

@Component({
  selector: 'app-integrations-overview',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    NzButtonModule,
    NzCardModule,
    NzTagModule,
    NzTableModule,
    NzInputModule,
    NzSelectModule,
    NzModalModule,
    NzToolTipModule,
    NzAvatarModule,
    TranslateModule
  ],
  templateUrl: './integrations-overview.component.html',
  styleUrls: ['./integrations-overview.component.less']
})
export class IntegrationsOverviewComponent implements OnInit {
  loading = false;

  providers: IntegrationProviderCatalogItem[] = [];
  connections: IntegrationConnection[] = [];
  integrationsAllowed = 0;
  integrationsUsed = 0;

  // UI state
  search = '';
  filter: StatusFilter = 'all';

  constructor(
    private integrationsService: IntegrationsService,
    private modal: NzModalService,
    private message: NzMessageService,
    private router: Router,
    private authService: AuthService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.reloadAll();
  }

  reloadAll(): void {
    this.loading = true;
    forkJoin({
      providers: this.integrationsService.listProviders(),
      connections: this.integrationsService.listConnections(),
    }).subscribe({
      next: ({ providers, connections }) => {
        this.providers = providers || [];
        this.connections = connections?.items || [];
        this.integrationsAllowed = Number(connections?.limits?.allowed || 0);
        this.integrationsUsed = Number(connections?.limits?.used || 0);
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.message.error(this.translate.instant('INTEGRATION.OVERVIEW.ERROR_LOAD'));
      }
    });
  }

  get hasCredentialsWritePermission(): boolean {
    return this.authService.hasPermission('company.integrations.credentials.write');
  }

  get canCreateNewConnection(): boolean {
    return this.integrationsUsed < this.integrationsAllowed;
  }

  get integrationsLimitReached(): boolean {
    return this.integrationsUsed >= this.integrationsAllowed;
  }

  // provider-first mapping
  getConnection(providerKey: string): IntegrationConnection | undefined {
    return (this.connections || []).find(c => (c.provider || '').toLowerCase() === (providerKey || '').toLowerCase());
  }

  providerCardVisible(p: IntegrationProviderCatalogItem): boolean {
    const name = (p.displayName || '').toLowerCase();
    const key = (p.provider || '').toLowerCase();
    const q = (this.search || '').trim().toLowerCase();

    const conn = this.getConnection(p.provider);
    if (!p.customerVisible && !conn) {
      return false;
    }
    const connected = !!conn;
    const needsAttention = conn?.status === 'error';

    const matchesSearch = !q || name.includes(q) || key.includes(q);
    const matchesFilter =
      this.filter === 'all' ||
      (this.filter === 'connected' && connected) ||
      (this.filter === 'not_connected' && !connected) ||
      (this.filter === 'needs_attention' && !!needsAttention);

    return matchesSearch && matchesFilter;
  }

  // display helpers
  tagColor(status: string): string {
    if (status === 'connected') return 'green';
    if (status === 'error') return 'red';
    if (status === 'disconnected') return 'default';
    return 'default';
  }

  providerStatusTag(p: IntegrationProviderCatalogItem, c?: IntegrationConnection): { text: string; color: string } {
    if (!this.isProviderAvailable(p)) return { text: this.translate.instant('INTEGRATION.COMING_SOON'), color: 'default' };
    if (!c) return { text: this.translate.instant('INTEGRATION.OVERVIEW.FILTER.NOT_CONNECTED'), color: 'default' };
    if (this.isSftpConnection(c) && c.sftpLifecycleStatus) {
      return {
        text: this.sftpLifecycleLabel(c.sftpLifecycleStatus),
        color: c.sftpLifecycleStatus === 'active' ? 'green' : c.sftpLifecycleStatus === 'error' ? 'red' : 'default',
      };
    }
    return { text: c.status, color: this.tagColor(c.status) };
  }

  isProviderAvailable(p: IntegrationProviderCatalogItem): boolean {
    return p.status === 'available';
  }

  capabilitiesLabel(p: IntegrationProviderCatalogItem): string {
    const caps = p.capabilities || [];
    if (!caps.length) return '-';
    return caps.map((capability: IntegrationCapability) => this.getCapabilityLabel(capability)).join(', ');
  }

  getProviderDescription(provider: IntegrationProviderCatalogItem): string {
    if (provider.descriptionKey) {
      return this.translate.instant(provider.descriptionKey);
    }
    return provider.description || this.translate.instant('INTEGRATION.OVERVIEW.PROVIDER_DEFAULT_DESC');
  }

  private getCapabilityLabel(capability: IntegrationCapability): string {
    if (capability.labelKey) {
      return this.translate.instant(capability.labelKey);
    }
    return capability.label || capability.key;
  }

  // SaaS actions
  primaryCtaLabel(p: IntegrationProviderCatalogItem): string {
    const conn = this.getConnection(p.provider);
    if (!this.isProviderAvailable(p)) return this.translate.instant('INTEGRATION.COMING_SOON');
    if (!conn) return this.translate.instant('INTEGRATION.OVERVIEW.CONNECT');
    if (conn.status === 'error') return this.translate.instant('INTEGRATION.OVERVIEW.FIX');
    return this.translate.instant('INTEGRATION.OVERVIEW.MANAGE');
  }

  primaryCta(p: IntegrationProviderCatalogItem): void {
    const conn = this.getConnection(p.provider);
    if (!this.isProviderAvailable(p)) return;

    if (!conn) {
      if (!this.hasCredentialsWritePermission) {
        this.message.warning(this.translate.instant('ERRORS.INTEGRATIONS_ADMIN_ONLY'));
        return;
      }
      if (this.integrationsLimitReached) {
        this.message.warning(this.translate.instant('ERRORS.INTEGRATIONS_LIMIT_REACHED_CONTACT'));
        return;
      }
      // go to setup wizard by provider (connect)
      this.router.navigate(['/company/integrations', p.provider, 'setup']);
      return;
    }

    // manage -> status
    this.router.navigate(['/company/integrations', conn.id, 'status']);
  }

  goToSetup(p: IntegrationProviderCatalogItem): void {
    if (!this.isProviderAvailable(p)) {
      return;
    }
    if (!this.hasCredentialsWritePermission) {
      this.message.warning(this.translate.instant('ERRORS.INTEGRATIONS_ADMIN_ONLY'));
      return;
    }
    if (!this.getConnection(p.provider) && this.integrationsLimitReached) {
      this.message.warning(this.translate.instant('ERRORS.INTEGRATIONS_LIMIT_REACHED_CONTACT'));
      return;
    }
    this.router.navigate(['/company/integrations', p.provider, 'setup']);
  }

  goToStatus(conn: IntegrationConnection): void {
    this.router.navigate(['/company/integrations', conn.id, 'status']);
  }

  quickTest(conn: IntegrationConnection): void {
    this.integrationsService.testConnection(conn.id).subscribe({
      next: (res) => this.message.success(res.ok ? this.translate.instant('INTEGRATION.OVERVIEW.CONNECTION_OK') : (res.message || this.translate.instant('INTEGRATION.OVERVIEW.CONNECTION_FAILED'))),
      error: (err) => {
        if (shouldSuppressGenericError(err)) {
          return;
        }
        this.message.error(err?.error?.message || this.translate.instant('INTEGRATION.OVERVIEW.ERROR_TEST'));
      }
    });
  }

  quickSync(conn: IntegrationConnection): void {
    this.integrationsService.runSync(conn.id).subscribe({
      next: () => this.message.success(this.translate.instant('INTEGRATION.OVERVIEW.SYNC_STARTED')),
      error: (err) => {
        if (shouldSuppressGenericError(err)) {
          return;
        }
        this.message.error(err?.error?.message || this.translate.instant('INTEGRATION.OVERVIEW.ERROR_SYNC'));
      }
    });
  }

  isSftpConnection(conn: IntegrationConnection): boolean {
    return conn.provider === 'sftp';
  }

  connectionActivityAt(conn: IntegrationConnection): string | null | undefined {
    return this.isSftpConnection(conn) ? conn.sftpLastImportFinishedAt : conn.lastSyncAt;
  }

  formatSftpDateTime(value: SftpDateTimeValue): string {
    return formatSftpDateTimeValue(value, this.translate.currentLang || this.translate.defaultLang);
  }

  sftpLifecycleLabel(status?: IntegrationConnection['sftpLifecycleStatus']): string {
    return this.translate.instant(`INTEGRATION.SFTP.LIFECYCLE_LABELS.${status || 'error'}`);
  }

  disconnect(conn: IntegrationConnection): void {
    this.modal.confirm({
      nzTitle: this.translate.instant('INTEGRATION.OVERVIEW.DISCONNECT_TITLE'),
      nzContent: this.translate.instant('INTEGRATION.OVERVIEW.DISCONNECT_DESC', { provider: conn.provider }),
      nzOkText: this.translate.instant('INTEGRATION.STATUS.DISCONNECT'),
      nzOkDanger: true,
      nzOnOk: () => {
        return new Promise<void>((resolve, reject) => {
          const disconnectRequest: Observable<unknown> = this.isSftpConnection(conn)
            ? this.integrationsService.changeSftpLifecycle(conn.id, 'disconnect')
            : this.integrationsService.deleteConnection(conn.id);
          disconnectRequest.subscribe({
            next: () => {
              this.message.success(this.translate.instant('INTEGRATION.OVERVIEW.DISCONNECTED'));
              this.reloadAll();
              resolve();
            },
            error: (err: unknown) => {
              if (shouldSuppressGenericError(err)) {
                reject();
                return;
              }
              const requestError = err as { error?: { message?: string } };
              this.message.error(requestError.error?.message || this.translate.instant('INTEGRATION.OVERVIEW.ERROR_DISCONNECT'));
              reject();
            }
          });
        });
      }
    });
  }

  scopeCount(conn: IntegrationConnection): number {
    return conn?.settings?.scope?.userIds?.length || 0;
  }

  enabledModulesCount(conn: IntegrationConnection): number {
    if (this.isSftpConnection(conn)) {
      return 3;
    }
    return conn?.settings?.enabledCapabilities?.length || 0;
  }

  syncMode(conn: IntegrationConnection): string {
    return conn?.settings?.syncMode || 'manual';
  }
}
